import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calculator, RefreshCw, AlertTriangle, Calendar, ArrowRight } from 'lucide-react';

export const GstLateFeesCalculatorPage = () => {
  const [formData, setFormData] = useState({
    pendingFromMonth: '',
    pendingFromYear: '',
    filingMonth: '',
    filingYear: '',
    taxLiability: '',
    isNilReturn: 'no',
  });
  const [result, setResult] = useState(null);

  const months = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' },
    { value: '03', label: 'March' }, { value: '04', label: 'April' },
    { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' },
    { value: '09', label: 'September' }, { value: '10', label: 'October' },
    { value: '11', label: 'November' }, { value: '12', label: 'December' },
  ];

  const years = ['2024', '2025', '2026'];

  const calculateLateFees = () => {
    if (!formData.pendingFromMonth || !formData.pendingFromYear || 
        !formData.filingMonth || !formData.filingYear) {
      alert('Please select all month and year fields');
      return;
    }

    // Due date is 20th of next month
    const pendingDate = new Date(parseInt(formData.pendingFromYear), parseInt(formData.pendingFromMonth), 20);
    const filingDate = new Date(parseInt(formData.filingYear), parseInt(formData.filingMonth) - 1, 20);

    if (filingDate <= pendingDate) {
      setResult({ daysLate: 0, lateFee: 0, interest: 0, totalPenalty: 0, message: 'Filed on time! No late fee applicable.' });
      return;
    }

    const diffTime = Math.abs(filingDate - pendingDate);
    const daysLate = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const taxLiability = parseFloat(formData.taxLiability) || 0;
    const isNil = formData.isNilReturn === 'yes' || taxLiability === 0;

    // Late fee: Nil=₹20/day max ₹500, Regular=₹50/day max ₹5000
    const lateFeePerDay = isNil ? 20 : 50;
    const maxLateFee = isNil ? 500 : 5000;
    const lateFee = Math.min(daysLate * lateFeePerDay, maxLateFee);

    // Interest 18% p.a.
    const interest = taxLiability > 0 ? Math.round((taxLiability * 0.18 * daysLate) / 365) : 0;

    setResult({ daysLate, lateFeePerDay, lateFee, interest, totalPenalty: lateFee + interest, isNil });
  };

  const handleReset = () => {
    setFormData({ pendingFromMonth: '', pendingFromYear: '', filingMonth: '', filingYear: '', taxLiability: '', isNilReturn: 'no' });
    setResult(null);
  };

  const formatCurrency = (num) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(num);

  return (
    <div data-testid="gst-late-fees-page" className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-6">
              <Calculator className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              GST GSTR-3B Late Fee <span className="text-gradient">Calculator</span>
            </h1>
            <p className="text-slate-400">Calculate late fees and interest for delayed GSTR-3B filing.</p>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Input Form */}
            <div className="bg-navy-800 border border-white/10 rounded-2xl p-6 md:p-8">
              <h2 className="font-heading font-semibold text-xl text-white mb-6">GST GSTR-3B Late Fee Calculator</h2>
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-slate-300">Return Pending From (Month-Year)</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Select value={formData.pendingFromMonth} onValueChange={(v) => setFormData({...formData, pendingFromMonth: v})}>
                      <SelectTrigger data-testid="pending-month" className="bg-navy-900 border-white/10 text-white">
                        <SelectValue placeholder="Month" />
                      </SelectTrigger>
                      <SelectContent className="bg-navy-800 border-white/10">
                        {months.map(m => <SelectItem key={m.value} value={m.value} className="text-white hover:bg-navy-700">{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={formData.pendingFromYear} onValueChange={(v) => setFormData({...formData, pendingFromYear: v})}>
                      <SelectTrigger data-testid="pending-year" className="bg-navy-900 border-white/10 text-white">
                        <SelectValue placeholder="Year" />
                      </SelectTrigger>
                      <SelectContent className="bg-navy-800 border-white/10">
                        {years.map(y => <SelectItem key={y} value={y} className="text-white hover:bg-navy-700">{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Filing In (Month-Year)</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Select value={formData.filingMonth} onValueChange={(v) => setFormData({...formData, filingMonth: v})}>
                      <SelectTrigger data-testid="filing-month" className="bg-navy-900 border-white/10 text-white">
                        <SelectValue placeholder="Month" />
                      </SelectTrigger>
                      <SelectContent className="bg-navy-800 border-white/10">
                        {months.map(m => <SelectItem key={m.value} value={m.value} className="text-white hover:bg-navy-700">{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={formData.filingYear} onValueChange={(v) => setFormData({...formData, filingYear: v})}>
                      <SelectTrigger data-testid="filing-year" className="bg-navy-900 border-white/10 text-white">
                        <SelectValue placeholder="Year" />
                      </SelectTrigger>
                      <SelectContent className="bg-navy-800 border-white/10">
                        {years.map(y => <SelectItem key={y} value={y} className="text-white hover:bg-navy-700">{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Monthly Tax Liability (₹) - Optional</Label>
                  <Input type="number" data-testid="tax-liability" value={formData.taxLiability} onChange={(e) => setFormData({...formData, taxLiability: e.target.value})} placeholder="Enter tax amount (0 for nil)" className="bg-navy-900 border-white/10 text-white" />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Is Nil Return?</Label>
                  <Select value={formData.isNilReturn} onValueChange={(v) => setFormData({...formData, isNilReturn: v})}>
                    <SelectTrigger className="bg-navy-900 border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-navy-800 border-white/10">
                      <SelectItem value="no" className="text-white hover:bg-navy-700">No (Regular Return)</SelectItem>
                      <SelectItem value="yes" className="text-white hover:bg-navy-700">Yes (Nil Return)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button data-testid="calculate-btn" onClick={calculateLateFees} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-6">
                    <Calculator className="mr-2 w-5 h-5" />Calculate Late Fees
                  </Button>
                  <Button onClick={handleReset} variant="outline" className="border-white/20 text-white hover:bg-white/10 py-6">
                    <RefreshCw className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Result */}
            <div className="bg-navy-800 border border-white/10 rounded-2xl p-6 md:p-8">
              {result ? (
                <div data-testid="result">
                  <h2 className="font-heading font-semibold text-xl text-white mb-6">Late Fee Calculation</h2>
                  {result.message ? (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 text-center">
                      <p className="text-green-400 font-medium">{result.message}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-slate-400">Total Days Late</span>
                        <span className="text-red-400 font-semibold">{result.daysLate} days</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-slate-400">Return Type</span>
                        <span className="text-white">{result.isNil ? 'Nil Return' : 'Regular Return'}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-slate-400">Late Fee Rate</span>
                        <span className="text-white">₹{result.lateFeePerDay}/day</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-slate-400">Total Late Fee</span>
                        <span className="text-orange-400 font-semibold">{formatCurrency(result.lateFee)}</span>
                      </div>
                      {result.interest > 0 && (
                        <div className="flex justify-between py-2 border-b border-white/10">
                          <span className="text-slate-400">Interest (18% p.a.)</span>
                          <span className="text-orange-400 font-semibold">{formatCurrency(result.interest)}</span>
                        </div>
                      )}
                      <div className="flex justify-between py-3 bg-red-500/10 rounded-lg px-4 mt-4">
                        <span className="text-white font-medium">Total Penalty</span>
                        <span className="text-red-400 font-bold text-lg">{formatCurrency(result.totalPenalty)}</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full min-h-[350px]">
                  <Calendar className="w-16 h-16 text-slate-600 mb-4" />
                  <h3 className="font-heading font-semibold text-xl text-white mb-2">Select Period</h3>
                  <p className="text-slate-400 text-center">Choose pending and filing months to calculate late fees</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Fee Structure */}
      <section className="py-12 md:py-16 bg-navy-800/50">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-heading font-bold text-2xl text-white text-center mb-8">GST Late Fee Structure</h2>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-navy-800 border border-green-500/20 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-green-400 mb-3">Nil Return</h3>
              <p className="text-white text-2xl font-bold mb-2">₹20/day</p>
              <p className="text-slate-400 text-sm">₹10 CGST + ₹10 SGST</p>
              <p className="text-slate-500 text-xs mt-2">Maximum: ₹500</p>
            </div>
            <div className="bg-navy-800 border border-orange-500/20 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-orange-400 mb-3">Regular Return</h3>
              <p className="text-white text-2xl font-bold mb-2">₹50/day</p>
              <p className="text-slate-400 text-sm">₹25 CGST + ₹25 SGST</p>
              <p className="text-slate-500 text-xs mt-2">Maximum: ₹5,000</p>
            </div>
            <div className="bg-navy-800 border border-purple-500/20 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-purple-400 mb-3">Annual (GSTR-9)</h3>
              <p className="text-white text-2xl font-bold mb-2">₹200/day</p>
              <p className="text-slate-400 text-sm">₹100 CGST + ₹100 SGST</p>
              <p className="text-slate-500 text-xs mt-2">Maximum: ₹5,000</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0" />
              <div>
                <h3 className="font-heading font-semibold text-lg text-white mb-3">Important Notes</h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                  <li className="flex items-start gap-2"><ArrowRight className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" /><span>Late fee charged from day after due date until filing date</span></li>
                  <li className="flex items-start gap-2"><ArrowRight className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" /><span>Interest @ 18% p.a. on outstanding tax liability</span></li>
                  <li className="flex items-start gap-2"><ArrowRight className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" /><span>Subsequent returns cannot be filed until previous returns cleared</span></li>
                  <li className="flex items-start gap-2"><ArrowRight className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" /><span>Non-filing can lead to GST registration cancellation</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-navy-800/50 border border-white/5 rounded-xl p-4">
            <p className="text-slate-500 text-xs text-center">
              <strong>Disclaimer:</strong> This calculator provides an estimate. Actual calculations may vary. Consult a tax professional.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
